import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { skipWhile, tap } from 'rxjs';
import { DwsmConfig } from 'src/app/analysis/models/DwsmConfig';
import { reAnalyzeWithCurrentInputs, setAdvancedSettingsPersonalDefaults } from 'src/app/dwsm/state/dwsm.actions';
import { selectActiveDwsmConfig, selectDwsmOverviewIsLoading } from 'src/app/dwsm/state/dwsm.selectors';
import { DWSMConfig } from 'src/app/masterdata/models/DWSMConfig';
import { findDwsmGlobalConfig } from 'src/app/masterdata/state/master-data.actions';
import { FormControlUtils } from '../../utils/form-control-utils';

@Component({
    selector: 'sera-dwsm-settings',
    templateUrl: './sera-dwsm-settings.component.html',
    styleUrls: ['./sera-dwsm-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SeraDwsmSettingsComponent {
    $selectImportDwsmConfig = this.store.select(selectActiveDwsmConfig).pipe(
        skipWhile(dwsmConfig => !dwsmConfig),
        tap(dwsmConfig => {
            this.patchForm(dwsmConfig!);
        })
    );

    $selectDwsmOverviewIsLoading = this.store.select(selectDwsmOverviewIsLoading);

    dwsmForm: UntypedFormGroup = this.formBuilder.group({
        dwsmConfigId: [null],
        shiftTime: [null, [Validators.required, Validators.min(0)]],
        minWalkingSpeed: [null, [Validators.required, Validators.min(0)]],
        minFastWalkingSpeed: [null, [Validators.required, Validators.min(0)]],
        minForceTime: [null, [Validators.required, Validators.min(0)]],
        minForceDetection: [null, [Validators.required, Validators.min(0)]],
        noFactor: [null],
        minHandMoveCR07: [null, [Validators.required, Validators.min(0)]],
        outlierDetectionFactor: [null, [Validators.required, Validators.min(0)]],
        factorDynNeckStress: [null, [Validators.required, Validators.min(0)]],
        smoothingQuarternions: [null, [Validators.required, Validators.min(0)]],
        smoothingPositions: [null, [Validators.required, Validators.min(0)]],
        accelerationThreshold: [null, [Validators.required, Validators.min(0)]],
        stepsToTrough: [null, [Validators.required, Validators.min(0)]],
        stepsToPeak: [null, [Validators.required, Validators.min(0)]],
        stepsAfterThreshold: [null, [Validators.required, Validators.min(0)]],
        correction: [null, [Validators.required, Validators.min(0)]]
    });

    constructor(private store: Store, private formBuilder: FormBuilder) {}

    patchForm(dwsmConfig: DWSMConfig): void {
        this.dwsmForm.patchValue({
            dwsmConfigId: dwsmConfig.dwsmConfigId,
            shiftTime: dwsmConfig.shiftTime,
            minWalkingSpeed: dwsmConfig.minWalkingSpeed,
            minFastWalkingSpeed: dwsmConfig.minFastWalkingSpeed,
            minForceTime: dwsmConfig.minForceTime,
            minForceDetection: dwsmConfig.minForceDetection,
            factorDynNeckStress: dwsmConfig.factorDynNeckStress,
            noFactor: dwsmConfig.noFactor,
            minHandMoveCR07: dwsmConfig.minHandMoveCR07,
            outlierDetectionFactor: dwsmConfig.outlierDetectionFactor,
            smoothingQuarternions: dwsmConfig.smoothingQuarternions,
            smoothingPositions: dwsmConfig.smoothingPositions,
            accelerationThreshold: dwsmConfig.accelerationThreshold,
            stepsToTrough: dwsmConfig.stepsToTrough,
            stepsToPeak: dwsmConfig.stepsToPeak,
            stepsAfterThreshold: dwsmConfig.stepsAfterThreshold,
            correction: dwsmConfig.correction
        });
    }

    updateFormClasses(formControlName: string): string {
        return FormControlUtils.updateStyleClassValidity(this.dwsmForm, formControlName);
    }

    onLoadGlobalDwsmGlobalConfig() {
        this.store.dispatch(findDwsmGlobalConfig());
    }

    buildDwsmConfigFromForm() {
        return { dwsmConfig: this.dwsmForm.value as DwsmConfig };
    }

    onSetPersonalDwsmGlobalConfig() {
        FormControlUtils.markAllControlsAsDirty(this.dwsmForm);
        if (this.dwsmForm.valid) {
            this.store.dispatch(setAdvancedSettingsPersonalDefaults(this.buildDwsmConfigFromForm()));
        }
    }

    onReAnalyzeWithCurrentInputs() {
        if (this.dwsmForm.valid) {
            this.store.dispatch(reAnalyzeWithCurrentInputs(this.buildDwsmConfigFromForm()));
        }
    }

    onNoFactorChange() {
        if (this.dwsmForm.value.noFactor) {
            this.dwsmForm.patchValue({ factorDynNeckStress: null });
        }
        this.updateValidatorFactorDynNeckStress();
    }

    updateValidatorFactorDynNeckStress() {
        if (!this.dwsmForm.value.noFactor) {
            this.dwsmForm.controls['factorDynNeckStress'].setValidators([Validators.required, Validators.min(0)]);
        } else {
            this.dwsmForm.controls['factorDynNeckStress'].clearValidators();
        }
        this.dwsmForm.controls['factorDynNeckStress'].updateValueAndValidity();
    }

    isFactorDynNeckStressDisabled(): boolean {
        return this.dwsmForm.value.noFactor;
    }
}
